import { Link } from "react-router-dom";
import { ArrowRight, Users, MapPin, Gift } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import { NewsletterForm } from "@/components/site/NewsletterForm";
import { IntroVideo } from "@/components/sections/IntroVideo";

// { t: "p" | "lead" | "beat" }  — beat = short emphatic one-liner
const STORY = [
  { t: "lead", text: "Hi, and firstly, before anything else, thank you for showing an interest in visiting my website." },
  { t: "p", text: "Below you can see my introduction video on YouTube, which hopefully explains what I am trying to do. But if you would rather read my story, my hopes and my dreams, then please read on." },
  { t: "p", text: "My love of fish tanks stems from when I was a child. I remember having several tanks around the house. In the living room we had a 4ft fish tank. Nothing like today's setups though. It was basically sat on blocks with a board over the top of it. It was a 4ft x 2ft x 2ft tank with, if I remember correctly, an undergravel filter." },
  { t: "p", text: "And again, if I remember correctly, the stocking was almost hideous!" },
  { t: "p", text: "We had a goldfish in the tank, in a warm-water setup, that grew absolutely huge, alongside guppies, sailfin mollies, black widow tetras and huge plecs with slate caves. Looking back now, I probably shouldn't admit half of what was in that tank, but that is where my love of fish really started." },
  { t: "p", text: "We had a second tank in the hallway. This had one large Oscar, one large kissing gourami, a 12\" plec and the largest red-tail shark I have ever seen." },
  { t: "beat", text: "I was obsessed." },
  { t: "p", text: "There was just something about fish that completely mesmerised me, and I desperately wanted my own tank." },
  { t: "p", text: "Unfortunately, I wasn't allowed one!" },
  { t: "p", text: "Then I turned 16, got myself a job at McDonald's straight out of school, and that was it. There was a new fish shop that had opened in Fleetwood. I cannot, for the life of me, remember the name of it, but they had discus in and I was in love." },
  { t: "p", text: "I didn't have the money to buy them, or even a tank, so the owner let me pay it off weekly. So I started working every hour God gave me because I wanted this fish tank." },
  { t: "p", text: "We had agreed that I was going to buy his breeding pair of Blue Turquoise Discus from his personal aquarium, so I just kept working every hour I could. I wanted to get it paid off sooner rather than later." },
  { t: "p", text: "Eventually, I had enough. The tank and accessories were paid off, and now it was all about getting the parameters right before the discus came." },
  { t: "beat", text: "Exciting times!" },
  { t: "p", text: "The day finally came for me to pick up the discus. His aquarium was simple but beautiful. It was like a big bow-front tank, similar to the Juwel ones. In the centre of the tank he had a giant piece of bogwood, with really bushy plants either side." },
  { t: "p", text: "It looked amazing." },
  { t: "p", text: "I got the discus home and settled them in, and within a week they had bred..." },
  { t: "p", text: "I got wrigglers, but unfortunately, as a young, naïve 16-year-old, I didn't know that you had to feed them supplementary food and, well, they just slowly died off." },
  { t: "p", text: "Then the adults got HITH and died over the next two weeks." },
  { t: "p", text: "I closed the tank down and that was it for a few years, until I was around 26." },
  { t: "p", text: "Myself and my partner at the time decided to get a fish tank for our son to watch as he grew. One tank turned into two, and before we knew it there were tanks in every room." },
  { t: "p", text: "Our weekends consisted of driving around fish shops. Literally every weekend." },
  { t: "p", text: "Then came a game changer." },
  { t: "p", text: "We had gone into a shop, Norbreck Aquatics, and someone had dropped off about 30 Malawi cichlids. They had put them all in one tank and the listing was simply: \"All fish in this tank £35.\"" },
  { t: "p", text: "I couldn't say no." },
  { t: "p", text: "Coincidentally, earlier that day I had been in Maidenhead Aquatics in Blackpool and they had a 5ft ex-display fish tank for sale for £200." },
  { t: "p", text: "This must have been around January because it was my then-partner's birthday soon. I know this because, for her birthday, I bought \"her\" a 5ft ex-display fish tank. Haha." },
  { t: "p", text: "To this day, that Malawi tank was definitely the nicest tank I have ever had. I absolutely loved the activity in it. There was always something happening." },
  { t: "p", text: "We had multiple breeding tanks on the go. There were tanks in every room. We had breeders everywhere." },
  { t: "p", text: "Then that relationship came to an abrupt end, and so did my fishkeeping for the next seven years." },
  { t: "beat", text: "Until I started again." },
  { t: "p", text: "I now have three tanks in my office, with a fourth and a rack about to be set up, and another three in my living room." },
  { t: "p", text: "The fish range from Acara to Apistogramma, peppermint plecos, calico convicts, Geophagus and Discus, to Red-Breasted Acara." },
  { t: "p", text: "And I'm building." },
  { t: "p", text: "My next step is to try and build what you see below." },
];

const Prose = ({ items }) => (
  <div className="space-y-6">
    {items.map((item, i) => {
      if (item.t === "beat")
        return <p key={i} className="font-display font-extrabold text-2xl sm:text-3xl text-brand-cyan leading-tight">{item.text}</p>;
      if (item.t === "lead")
        return <p key={i} className="text-white text-lg sm:text-xl leading-relaxed font-medium">{item.text}</p>;
      return <p key={i} className="text-brand-textSec text-[17px] leading-relaxed">{item.text}</p>;
    })}
  </div>
);

export const PersonalStory = ({ showLabel = true }) => {
  return (
    <div data-testid="personal-story">
      {/* Intro video + opening */}
      <section className="pt-16 lg:pt-24 pb-4">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            {showLabel && <SectionLabel>My Story</SectionLabel>}
            <div className="mt-6">
              <IntroVideo />
            </div>
            <p className="text-brand-textMuted text-xs mt-3 text-center">Prefer to read? The full story is below.</p>
          </Reveal>
        </div>
      </section>

      {/* Story prose */}
      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <Prose items={STORY} />
          </Reveal>
        </div>
      </section>

      {/* The Dream */}
      <section className="py-16 lg:py-24 bg-brand-surface" data-testid="story-dream">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <SectionLabel>The Dream</SectionLabel>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-white leading-tight">
              I WANT TO TAKE THE COMMUNITY WITH ME
            </h2>
            <div className="mt-8 space-y-6">
              <p className="text-brand-textSec text-[17px] leading-relaxed">
                My dream is to build a YouTube channel and, more importantly, a community that will eventually
                allow me to build a dedicated fish room or unit.
              </p>
              <p className="text-brand-textSec text-[17px] leading-relaxed">
                But I don't just want to build it myself. I want to take the community with me.
              </p>
              <p className="text-brand-textSec text-[17px] leading-relaxed">
                If this works and I get to a stage where I can start getting filters, equipment and other
                products to test, then from day one my condition will be that if a company gives me something
                to test, I want one of those products to be given away to our community.
              </p>
              <p className="font-display font-bold text-xl text-brand-cyan">Because this is an "our", not a "my".</p>
              <p className="text-brand-textSec text-[17px] leading-relaxed">
                For this to work, I need the help of the followers. I cannot do it alone. I want the people who
                follow the journey to have a genuine say in what happens.
              </p>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                "It will be you who decides what fish we buy and try to breed next.",
                "It will be you who decides what we invest in next.",
                "It will be you who sees the good calls, the bad calls, the wins and the failures.",
              ].map((line, i) => (
                <div key={i} className="card-surface p-5">
                  <span className="h-9 w-9 grid place-items-center rounded-full bg-brand-cyan/10 text-brand-cyan mb-3"><Users size={18} /></span>
                  <p className="text-white text-sm leading-relaxed">{line}</p>
                </div>
              ))}
            </div>

            <p className="mt-10 font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight">
              This isn't just about me building a fish room.<br />
              <span className="text-brand-cyan">It's about us building it together.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Giving Back */}
      <section className="py-16 lg:py-24" data-testid="story-giving-back">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Giving Back</SectionLabel>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-white leading-tight">THE FISH HOUSE ON TOUR</h2>
            <div className="mt-8 space-y-6">
              <p className="text-brand-textSec text-[17px] leading-relaxed">
                I also want to give something back to the fishkeeping community. One of the ongoing features I
                want to create will be <span className="text-white font-medium">"The Fish House On Tour"</span> — and this will be community-led.
              </p>
              <p className="text-brand-textSec text-[17px] leading-relaxed">
                You tell me which LFS you want me to visit — just drop the shop name in the comments on my
                YouTube videos. I'll speak to them, visit the store, sit down with the owner and find out what
                got them into fishkeeping, why they started the shop and what keeps them doing it. Then we'll
                have a walk around the store and see what they've got.
              </p>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: MapPin, t: "You pick the shop", d: "Suggest your favourite independent fish shop in the comments on my YouTube videos." },
                { icon: Users, t: "Meet the people", d: "Hear the stories behind the shops and the people who run them." },
                { icon: Gift, t: "A real platform", d: "Give hidden-gem independents somewhere to show what they're about." },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} className="card-surface p-5">
                  <span className="h-9 w-9 grid place-items-center rounded-full bg-brand-cyan/10 text-brand-cyan mb-3"><Icon size={18} /></span>
                  <p className="text-white font-medium text-sm">{t}</p>
                  <p className="text-brand-textSec text-sm mt-1 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>

            <p className="mt-10 font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight">
              Because ultimately, this isn't just my project. It's ours.<br />
              <span className="text-brand-cyan">We are in this together.</span>
            </p>

            <div className="mt-10"><NewsletterForm /></div>

            <div className="mt-10">
              <Link to="/journey" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="story-journey-btn">
                Follow The Journey <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
